import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Input } from '@/components/ui/input';
import {
  Users,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  Search,
  ChevronRight,
  Clock,
  Activity,
  Star
} from 'lucide-react';
import api from '../lib/axios';
import { toast } from 'sonner';

function IkonsAdminPanel() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (user?.role !== 'agent') {
      navigate('/dashboard');
      return;
    }
    fetchPlayersOverview();
  }, [user, navigate]);

  const fetchPlayersOverview = async () => {
    try {
      const response = await api.get('/ikons/admin/players-overview');
      setPlayers(response.data);
    } catch (error) {
      console.error('Error fetching players:', error);
      toast.error('Error al cargar jugadores');
    } finally {
      setLoading(false);
    }
  };

  const filteredPlayers = useMemo(() => {
    let result = players;
    
    if (filter === 'alert') {
      result = result.filter(p => p.latest_score?.status === 'red' || p.latest_score?.status === 'yellow');
    } else if (filter === 'optimal') {
      result = result.filter(p => p.latest_score?.status === 'green');
    } else if (filter === 'inactive') {
      result = result.filter(p => !p.latest_score);
    }
    
    if (searchQuery) {
      result = result.filter(player => 
        player.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        player.email?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    return result.sort((a, b) => {
      const statusOrder = { red: 0, yellow: 1, green: 2, undefined: 3 };
      const aStatus = statusOrder[a.latest_score?.status] ?? 3;
      const bStatus = statusOrder[b.latest_score?.status] ?? 3;
      if (aStatus !== bStatus) return aStatus - bStatus;
      return (b.latest_score?.total_score || 0) - (a.latest_score?.total_score || 0);
    });
  }, [players, filter, searchQuery]);

  const stats = useMemo(() => ({
    total: players.length,
    alert: players.filter(p => p.latest_score?.status === 'red' || p.latest_score?.status === 'yellow').length,
    optimal: players.filter(p => p.latest_score?.status === 'green').length,
    inactive: players.filter(p => !p.latest_score).length,
  }), [players]);

  const getStatusIndicator = (status) => {
    switch (status) {
      case 'red': return 'bg-red-500';
      case 'yellow': return 'bg-amber-500';
      case 'green': return 'bg-green-500';
      default: return 'bg-gray-400';
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'down': return <TrendingDown className="w-4 h-4 text-red-500" />;
      default: return <Minus className="w-4 h-4 text-gray-400" />;
    }
  };

  const getLastActivity = (player) => {
    if (!player.last_activity) return 'Sin actividad';
    const date = new Date(player.last_activity);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Hoy';
    if (diffDays === 1) return 'Ayer';
    if (diffDays < 7) return `Hace ${diffDays} días`;
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  if (user?.role !== 'agent') return null;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-10 h-10 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-[#F8F9FA]" data-testid="ikons-admin-panel">
        {/* Header */}
        <div className="bg-white border-b border-gray-100 px-4 lg:px-6 py-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-xl font-bold text-[#4F4F4F]">Mis Jugadores</h1>
                <p className="text-sm text-[#808080]">{stats.total} jugadores</p>
              </div>
            </div>
            
            {/* Filters */}
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
              <button
                onClick={() => setFilter('all')}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  filter === 'all' ? 'bg-[#4F4F4F] text-white' : 'bg-gray-100 text-[#808080]'
                }`}
              >
                Todos ({stats.total})
              </button>
              {stats.alert > 0 && (
                <button
                  onClick={() => setFilter('alert')}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                    filter === 'alert' ? 'bg-red-500 text-white' : 'bg-red-50 text-red-500'
                  }`}
                >
                  <AlertTriangle className="w-4 h-4" />
                  Alerta ({stats.alert})
                </button>
              )}
              <button
                onClick={() => setFilter('optimal')}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  filter === 'optimal' ? 'bg-green-500 text-white' : 'bg-green-50 text-green-600'
                }`}
              >
                Óptimo ({stats.optimal})
              </button>
              {stats.inactive > 0 && (
                <button
                  onClick={() => setFilter('inactive')}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    filter === 'inactive' ? 'bg-gray-500 text-white' : 'bg-gray-100 text-[#808080]'
                  }`}
                >
                  Sin datos ({stats.inactive})
                </button>
              )}
            </div>

            {/* Search */}
            <div className="relative mt-3">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#808080]" />
              <Input 
                placeholder="Buscar jugador..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-11 bg-gray-50 border-gray-200 text-[#4F4F4F] placeholder:text-gray-400 rounded-xl"
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 lg:px-6 py-4 pb-24 lg:pb-6">
          <div className="max-w-4xl mx-auto space-y-2">
            {filteredPlayers.length === 0 ? (
              <div className="bg-white p-8 rounded-xl border border-gray-100 text-center">
                <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-[#808080]">
                  {searchQuery ? 'Sin resultados' : 'No hay jugadores'}
                </p>
              </div>
            ) : (
              filteredPlayers.map((player) => (
                <button
                  key={player.user_id}
                  onClick={() => navigate(`/ikons/admin/player/${player.user_id}`)}
                  className={`w-full p-4 bg-white rounded-xl border border-gray-100 shadow-sm flex items-center gap-3 text-left active:scale-[0.98] transition-transform hover:border-[#D4AF37]/30 ${
                    player.latest_score?.status === 'red' ? 'border-l-4 border-l-red-500' :
                    player.latest_score?.status === 'yellow' ? 'border-l-4 border-l-amber-500' :
                    player.latest_score?.status === 'green' ? 'border-l-4 border-l-green-500' : ''
                  }`}
                  data-testid={`player-row-${player.user_id}`}
                >
                  {/* Avatar */}
                  <div className="relative">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A67C00] flex items-center justify-center text-white font-bold text-lg">
                      {player.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-white ${getStatusIndicator(player.latest_score?.status)}`} />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#4F4F4F] truncate">{player.name}</p>
                    <div className="flex items-center gap-3 mt-1">
                      {player.latest_score ? (
                        <div className="flex items-center gap-1">
                          <span className="text-lg font-bold text-[#4F4F4F]">{player.latest_score.total_score}</span>
                          <span className="text-xs text-[#808080]">/25</span>
                          {getTrendIcon(player.trend)}
                        </div>
                      ) : (
                        <span className="text-xs text-[#808080]">Sin score</span>
                      )}
                      
                      <span className="text-xs text-[#808080] flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {getLastActivity(player)}
                      </span>
                    </div>
                  </div>

                  <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0" />
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default IkonsAdminPanel;
